/**
 * Retours à l'utilisateur fournis par le socle (zone unique, montée une fois dans App) :
 *   - messages éphémères : `const afficherMessage = useMessageEphemere(); afficherMessage({ statut: 'succes', titre: t('…') })`
 *     (composant Toast du design, Radix Toast) ;
 *   - confirmation : `const confirmer = useConfirmation(); if (await confirmer({ titre: t('…'), danger: true })) …`
 *     (composant ConfirmDialog du design, Radix AlertDialog) – remplace window.confirm (boutons traduits, design AGORA).
 * Les textes sont fournis traduits par l'appelant ; boutons par défaut : « Valider » / « Annuler » (commun.*).
 */
import React from 'react';
import { ConfirmDialog, Toast, ToastZone, type StatutToast } from '../../design';
import { useT } from '../i18n';

export interface MessageEphemere {
  titre: string; texte?: string;
  /** succes (défaut), info ou danger (annoncé immédiatement aux lecteurs d'écran). */
  statut?: 'succes' | 'info' | 'danger';
  /** Action facultative : `alternative` = comment faire sans ce bouton (lu par les lecteurs d'écran). */
  action?: { libelle: string; alternative: string; executer: () => void };
  /** Durée d'affichage en ms (5 000 par défaut). */
  duree?: number;
}

export interface DemandeConfirmation {
  titre: string; texte?: string;
  /** Libellés des boutons (défaut : « Valider » / « Annuler »). */
  confirmer?: string; annuler?: string;
  /** Action destructive ou irréversible : bouton de confirmation rouge. */
  danger?: boolean;
}

interface Retours {
  afficher: (m: MessageEphemere) => void;
  confirmer: (d: DemandeConfirmation) => Promise<boolean>;
}

const Ctx = React.createContext<Retours | null>(null);
const STATUT: Record<NonNullable<MessageEphemere['statut']>, StatutToast> = { succes: 'success', info: 'info', danger: 'danger' };

let compteur = 0;

export function RetoursProvider({ children }: { children: React.ReactNode }) {
  const { t } = useT();
  const [messages, setMessages] = React.useState<(MessageEphemere & { id: number; ouvert: boolean })[]>([]);
  const [demande, setDemande] = React.useState<DemandeConfirmation | null>(null);
  // Réponse attendue par l'appelant de confirmer() ; une seule à la fois (la précédente vaut « Annuler »).
  const attente = React.useRef<((ok: boolean) => void) | null>(null);

  const valeur = React.useMemo<Retours>(() => ({
    afficher: (m) => setMessages((l) => [...l.slice(-2), { ...m, id: ++compteur, ouvert: true }]),
    confirmer: (d) => new Promise<boolean>((resoudre) => {
      attente.current?.(false);
      attente.current = resoudre;
      setDemande(d);
    }),
  }), []);

  // Appelée par le bouton choisi puis par la fermeture de la fenêtre : seule la première réponse compte.
  const repondre = (ok: boolean) => {
    attente.current?.(ok);
    attente.current = null;
    setDemande(null);
  };
  // Message fermé : retiré après l'animation de sortie éventuelle.
  const fermer = (id: number) => {
    setMessages((l) => l.map((m) => (m.id === id ? { ...m, ouvert: false } : m)));
    window.setTimeout(() => setMessages((l) => l.filter((m) => m.id !== id)), 400);
  };

  return (
    <Ctx.Provider value={valeur}>
      <ToastZone label={t('commun.zoneMessages', { touche: 'F8' })} itemLabel={t('commun.message')}>
        {children}
        {messages.map((m) => (
          <Toast
            key={m.id} open={m.ouvert} onOpenChange={(o) => { if (!o) fermer(m.id); }}
            title={m.titre} description={m.texte} status={STATUT[m.statut || 'succes']} duration={m.duree}
            action={m.action ? { label: m.action.libelle, altText: m.action.alternative, onClick: m.action.executer } : undefined}
            closeLabel={t('commun.fermer')}
          />
        ))}
      </ToastZone>
      {demande ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) repondre(false); }}
          title={demande.titre} description={demande.texte} danger={demande.danger}
          confirmLabel={demande.confirmer || t('commun.valider')} cancelLabel={demande.annuler || t('commun.annuler')}
          onConfirm={() => repondre(true)} onCancel={() => repondre(false)}
        />
      ) : null}
    </Ctx.Provider>
  );
}

function useRetours(): Retours {
  const r = React.useContext(Ctx);
  if (!r) throw new Error('RetoursProvider manquant');
  return r;
}

/** Message éphémère (« toast ») : confirmation brève d'une action, avertissement non bloquant. */
export function useMessageEphemere(): (m: MessageEphemere) => void {
  return useRetours().afficher;
}

/** Demande de confirmation dans une fenêtre du design ; résout `true` si l'utilisateur confirme. */
export function useConfirmation(): (d: DemandeConfirmation) => Promise<boolean> {
  return useRetours().confirmer;
}
